import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, merge, Observable, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged, finalize, first } from 'rxjs/operators'
import { TabConfig } from 'src/app/shared/components/tabs/tabs.component'
import { ApiService } from 'src/app/services/api.service'
import {
  AccessTemplatesDto,
  ApplicationType,
  AwsSourceResourceDto,
  RestoreTriggerDto,
  SyncPairAccessDto,
  SyncPairInfo,
  SyncPhaseDto,
} from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { durationText } from 'src/app/shared/utils'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { SelectionMatchType, Tag } from 'src/utils/types'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { BaseError } from 'src/utils/errors'
import { AccessScope, AccessService, AccessState } from 'src/app/services/access.service'
import { emailAddressIsValid } from 'src/utils/validators'
import { List } from 'immutable'
import { ResourceSelections } from 'src/app/shared/app-resources-selector/app-resources-selector.component'
import { ReleaseDetailsService } from './../../../../app/services/release-details.service'
import { ApplicationFormBase } from 'src/app/shared/application-form-base'
import { ApplicationFormBaseService } from 'src/app/shared/application-form-base.service'
import { RuleCount, RuleCountService } from 'src/app/shared/rule-count.service'
import { AppResourceRuleSelectorComponent } from 'src/app/shared/app-resource-rule-selector/app-resource-rule-selector.component'
import { AccessLevel, AccessLevelService, AppPermission } from 'src/app/services/access-level.service'
import { HasUnsavedChanges } from 'src/app/account/guards/unsaved-changes.guard'
import { Subject } from 'rxjs'
import {
  MigrationIntentService,
  AccessMethodType,
  ACCESS_METHOD,
} from 'src/app/account/services/migration-intent.service'

@Component({
  selector: 'app-application-editor',
  templateUrl: './application-editor.component.html',
  styleUrls: ['./application-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ApplicationEditorComponent
  extends ApplicationFormBase
  implements OnInit, OnDestroy, AfterViewInit, HasUnsavedChanges
{
  application: Application = null
  accessTemplates: AccessTemplatesDto = null
  accessCheckResults: SyncPairAccessDto = null
  AwsAccessScope = AccessScope
  AccessMethod = ACCESS_METHOD // Expose constants for template use
  testingAccess = false
  releaseDetails$ = this.releaseDetailsService.fetchReleaseDetails()

  // Tab management
  activeTabIndex: number = 0
  activeTab: 'general' | 'resources' | 'upgrade' = 'resources'
  private _tabs: TabConfig[] = []
  private _lastUpgradeNeeded: boolean = false
  private _lastUpgradeLoading: boolean = true
  private _lastNeedsAccessConfig: boolean = false

  // Access method selection in the form (local state before save)
  // Initialized from actual access state when app loads
  selectedAccessMethod: AccessMethodType = ACCESS_METHOD.CLOUDFORMATION
  // Track if user changed selection (for showing save prompt)
  accessMethodDirty = false

  inclusionRulesCount$: Observable<RuleCount>
  exclusionRulesCount$: Observable<RuleCount>

  private applicationIdSubscription: Subscription = null
  private appSubscription: Subscription = null
  private updateApplicationSubscription: Subscription = null
  private accessStatusSubscription: Subscription = null
  private deleteApplicationSubscription: Subscription = null
  private integrationsChangeSubscription: Subscription = null
  private sourceResourcesSubscription: Subscription = null
  private permissionsSubscription: Subscription = null
  private accessTemplatesSubscription: Subscription = null

  // Permission states
  canEditResourcesPermission = false
  canDeleteApplicationPermission = false
  canModifyApplication = false
  permissionsLoaded = false

  private rescanFlag = false

  // Subject for CanDeactivate guard - emits true to allow navigation, false to block
  private deactivateSubject = new Subject<boolean>()

  sourceAccessService: AccessService = null
  targetAccessService: AccessService = null

  // Prevents auto-switch from setup → update view when both accounts become managed mid-migration
  stackSetsSetupAcknowledged = false

  private sourceResources: AwsSourceResourceDto[] = null

  private _applicationFormModel = this.formBuilder.group(
    {
      applicationName: ['', Validators.required],
      applicationNotificationEmails: [new Array<string>()],
      applicationRpo: ['3600'],
      applicationIntegrationIds: [new Set<string>()],
      matchingRules: [new Array<Tag>()],
      excludingRules: [new Array<Tag>()],
      resourceSelections: [{ selectedResources: [], suppressedResources: [] } as ResourceSelections],
    },
    {
      validators: [this.checkForExistingAppWithSameName.bind(this)],
    }
  )

  get applicationFormModel(): UntypedFormGroup {
    return this._applicationFormModel
  }

  @ViewChild('deleteApplicationDialog', { static: true }) deleteApplicationDialog: ModalDialogComponent
  @ViewChild('unsavedChangesDialog', { static: true }) unsavedChangesDialog: ModalDialogComponent
  @ViewChild('inclusionTagSelector') inclusionTagSelector: AppResourceRuleSelectorComponent
  @ViewChild('exclusionTagSelector') exclusionTagSelector: AppResourceRuleSelectorComponent

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    protected apiService: ApiService,
    public uiService: UiService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private releaseDetailsService: ReleaseDetailsService,
    private applicationFormBaseService: ApplicationFormBaseService,
    private ruleCountService: RuleCountService,
    private accessLevelService: AccessLevelService,
    private migrationIntentService: MigrationIntentService
  ) {
    super(apiService)
  }

  ngOnInit() {
    // Initialize permissions
    this.initializePermissions()

    // Initialize with default observables, will be updated in ngAfterViewInit
    this.inclusionRulesCount$ = this.ruleCountService.getRuleCount$(this.applicationFormModel.get('matchingRules'))
    this.exclusionRulesCount$ = this.ruleCountService.getRuleCount$(this.applicationFormModel.get('excludingRules'))

    this.applicationIdSubscription = combineLatest([this.accountService.accountIdStream, this.route.paramMap])
      .pipe(finalize(() => (this.applicationIdSubscription = null)))
      .subscribe(([, paramMap]) => this.initializeApplication(paramMap.get('applicationId')))

    this.formChangeSubscription = merge(
      this.applicationFormModel.get('matchingRules').valueChanges.pipe(
        debounceTime(400), // if they're typing in the tag field, aggregate keystrokes before flooding update events
        distinctUntilChanged() // only raise an event if the value is changed
      ),
      this.applicationFormModel.get('excludingRules').valueChanges.pipe(
        debounceTime(400), // if they're typing in the tag field, aggregate keystrokes before flooding update events
        distinctUntilChanged() // only raise an event if the value is changed
      ),
      this.applicationFormModel.get('resourceSelections').valueChanges,
      this.applicationFormModel.get('applicationIntegrationIds').valueChanges
    ).subscribe(() => {
      this.updateResources()
    })

    this.integrationsChangeSubscription = this.applicationFormModel
      .get('applicationIntegrationIds')
      .valueChanges.pipe(
        debounceTime(3000) // wait 3 seconds after a change for other changes to come in
      )
      .subscribe(() => {
        this.queueRescan()
      })
  }

  ngAfterViewInit() {
    // The rule count observables are already properly set up in ngOnInit
    // and will automatically update when form values change
  }

  ngOnDestroy() {
    if (subscriptionActive(this.applicationIdSubscription)) {
      this.applicationIdSubscription.unsubscribe()
    }
    this.applicationIdSubscription = null

    if (subscriptionActive(this.appSubscription)) {
      this.appSubscription.unsubscribe()
    }
    this.appSubscription = null

    if (subscriptionActive(this.accessStatusSubscription)) {
      this.accessStatusSubscription.unsubscribe()
    }
    this.accessStatusSubscription = null

    if (subscriptionActive(this.formChangeSubscription)) {
      this.formChangeSubscription.unsubscribe()
    }
    this.formChangeSubscription = null

    if (subscriptionActive(this.integrationsChangeSubscription)) {
      this.integrationsChangeSubscription.unsubscribe()
    }
    this.integrationsChangeSubscription = null

    if (subscriptionActive(this.sourceResourcesSubscription)) {
      this.sourceResourcesSubscription.unsubscribe()
    }
    this.sourceResourcesSubscription = null

    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
    }
    this.permissionsSubscription = null

    if (subscriptionActive(this.accessTemplatesSubscription)) {
      this.accessTemplatesSubscription.unsubscribe()
    }
    this.accessTemplatesSubscription = null
  }

  private initializePermissions() {
    // Permissions will be checked per application when application is loaded
    // Initialize with false until we have the application ID
    this.canDeleteApplicationPermission = false
    this.canModifyApplication = false
    this.permissionsLoaded = false
  }

  private updatePermissionsForApplication(applicationId: string) {
    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe()
    }

    // Check UPDATE_APP permission for editing and DELETE_APP permission for deletion
    this.permissionsSubscription = combineLatest([
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.UPDATE_APP),
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.DELETE_APP),
    ]).subscribe(([canEdit, canDelete]) => {
      this.canDeleteApplicationPermission = canDelete
      // Users can modify if they have UPDATE_APP permission
      this.canModifyApplication = canEdit
      this.permissionsLoaded = true
    })
  }

  private initializeApplication(applicationId: string) {
    const accountId = this.accountService.accountId
    this.application = null

    // Reset accessMethodDirty when loading a new application
    this.accessMethodDirty = false

    // Initialize migration intent service with current account
    this.migrationIntentService.setAccountId(accountId)

    // Update permissions for this specific application
    this.updatePermissionsForApplication(applicationId)

    this.appSubscription = this.applicationProviderService
      .loadApplication$(applicationId, accountId)
      .pipe(
        first(),
        finalize(() => (this.appSubscription = null))
      )
      .subscribe(
        (application) => {
          // Use emitEvent: false to prevent triggering valueChanges during initial load
          // This prevents the form from being marked dirty during initialization
          this._applicationFormModel.setValue(
            {
              applicationName: application.name,
              applicationNotificationEmails: application.notificationEmails,
              applicationRpo: application.rpo.toString(),
              applicationIntegrationIds: application.integrationIds,
              matchingRules: application.matchingRules,
              excludingRules: application.excludingRules || new Array<Tag>(),
              resourceSelections: {
                selectedResources: Array.from(application.matchingArns),
                suppressedResources: Array.from(application.suppressedArns),
              } as ResourceSelections,
            },
            { emitEvent: false }
          )

          // Ensure form starts in pristine state
          this._applicationFormModel.markAsPristine()
          this._applicationFormModel.markAsUntouched()

          // Recreate the rule count observables after form is populated to ensure they emit current values
          this.inclusionRulesCount$ = this.ruleCountService.getRuleCount$(
            this.applicationFormModel.get('matchingRules')
          )
          this.exclusionRulesCount$ = this.ruleCountService.getRuleCount$(
            this.applicationFormModel.get('excludingRules')
          )

          // Always start with exclusion rules disabled/obscured on page load
          this.exclusionRulesEnabled = false

          this.setApplication(application)

          this.sourceResourcesSubscription = application
            .loadSourceResources$()
            .pipe(finalize(() => (this.sourceResourcesSubscription = null)))
            .subscribe(
              (sourceResources) => {
                this.sourceResources = sourceResources
                this._sourceResourcesLoaded = true
                this.updateResources()
              },
              (error) => {
                console.error('Failed to load source resources:', error)
                handleArpioError(window.location.href, this.apiService, this.uiService)(error)
                // Still update resources with empty list so UI doesn't hang
                this.sourceResources = []
                this._sourceResourcesLoaded = true
                this.updateResources()
              }
            )

          // Check the access templates to see if an upgrade is needed.  We launch this here,
          // instead of chaining it with the other observables, so that it doesn't block the UI.
          this.testingAccess = true
          this.accessStatusSubscription = this.apiService
            .testSyncPairAccess(this as SyncPairInfo)
            .pipe(
              finalize(() => {
                this.accessStatusSubscription = null
                this.testingAccess = false
              })
            )
            .subscribe(
              (accessDto) => {
                this.accessCheckResults = accessDto
                this.sourceAccessService = new AccessService(
                  this.apiService,
                  this.uiService,
                  this as SyncPairInfo,
                  AccessScope.SOURCE,
                  accessDto
                )
                this.targetAccessService = new AccessService(
                  this.apiService,
                  this.uiService,
                  this as SyncPairInfo,
                  AccessScope.TARGET,
                  accessDto
                )

                // Auto-acknowledge if both already managed on initial load (not mid-migration)
                if (this.sourceAccessService.managedByStackSet && this.targetAccessService.managedByStackSet) {
                  this.stackSetsSetupAcknowledged = true
                }

                // Initialize selected access method from actual state (if user hasn't changed it)
                if (!this.accessMethodDirty) {
                  this.selectedAccessMethod = this.currentAccessMethod
                }

                // Auto-clear migration intent if actual state now matches the intent
                const intent = this.migrationIntent
                if (intent !== null && intent === this.currentAccessMethod) {
                  this.migrationIntentService.clearMigrationIntent(this.sourceEndpointUri, this.targetEndpointUri)
                }

                // Force tabs to rebuild now that we know if upgrade is needed
                this._tabs = []
              },
              handleArpioError(window.location.href, this.apiService, this.uiService)
            )
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  private launchRescan() {
    this.appSubscription = this.application
      .rescanSourceResources$(this.applicationIntegrationIds)
      .pipe
      // DO NOT finalize(() => this.appSubscription = null) HERE
      // BECAUSE WE DON'T NULLIFY THE SUBSCRIPTION WHEN rescanFlag IS TRUE
      ()
      .subscribe(
        (application) => this.setApplication(application),
        (error) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
          this.appSubscription = null
        },
        () => {
          this.appSubscription = null // launchRescan() will set this again if rescanFlag is true
          if (this.rescanFlag) {
            this.rescanFlag = false // reset the flag
            this.launchRescan() // recurse
          }
        }
      )
  }

  private queueRescan() {
    if (!subscriptionActive(this.appSubscription)) {
      this.launchRescan()
    } else {
      this.rescanFlag = true // set the flag to scan again when the current scan completes.
    }
  }

  refreshButtonClick() {
    /**
     * Launch a server-side refresh of the Sync Source, and wait for it to complete.
     */
    this.queueRescan()
  }

  private setApplication(application: Application) {
    this.application = application
    this.sourceResources = application.sourceResources

    this.updateResources()
  }

  updateResources(): void {
    if (!this.sourceResources) {
      this._resources = List([])
      return
    }

    const resources = this.sourceResources.filter(() => true) // Keep existing filter logic
    const { selectedResources: matchingArns, suppressedResources: excludedWeakArns } = this.getResourceSelectionSets()
    this.updateResourceSelection(resources, matchingArns, excludedWeakArns, this.matchingRules, this.excludingRules)
  }

  saveApplication() {
    const { selectedResources: matchingArns, suppressedResources: excludedWeakArns } = this.getResourceSelectionSets()

    // If access method changed, persist migration intent to UIState
    if (this.accessMethodDirty && this.selectedAccessMethod !== this.currentAccessMethod) {
      this.migrationIntentService.setMigrationIntent(
        this.sourceEndpointUri,
        this.targetEndpointUri,
        this.selectedAccessMethod
      )
    } else if (this.accessMethodDirty && this.selectedAccessMethod === this.currentAccessMethod) {
      // User changed selection back to current state - clear any existing intent
      this.migrationIntentService.clearMigrationIntent(this.sourceEndpointUri, this.targetEndpointUri)
    }

    this.updateApplicationSubscription = this.application
      .update$(
        this.applicationName,
        this.rpo,
        this.notificationEmails,
        this.matchingRules,
        this.excludingRules,
        matchingArns,
        excludedWeakArns,
        this.applicationIntegrationIds,
        this.identityProviderId
      )
      .pipe(finalize(() => (this.updateApplicationSubscription = null)))
      .subscribe((): void => {
        // Reset dirty state before navigation to prevent unsaved changes dialog
        this._applicationFormModel.markAsPristine()
        this.accessMethodDirty = false

        // Inject optimistic backup resources into the Application's cache so the
        // app overview table shows new resources immediately with spinner status,
        // instead of stale data or skeleton rows while waiting for the backend.
        // Use the full set of selected resources (rules + explicit ARNs) from the
        // editor's resource list, not just matchingArns (which only has explicit ARNs).
        // matchingRules valueChanges has a 400ms debounce, so _resources may not yet
        // reflect a rule edit that immediately preceded Save — recompute synchronously
        // here so the optimistic list isn't missing newly-matched resources.
        if (this.sourceResources?.length > 0) {
          this.updateResources()
          const allSelectedArns = new Set<string>(
            this._resources
              .filter((r) => r.selectionType !== SelectionMatchType.NO_MATCH && !r.isExcluded)
              .map((r) => r.resourceArn)
              .toArray()
          )
          this.application.setOptimisticBackupResources(this.sourceResources, allSelectedArns)
        }

        // Signal that a backup is expected after save so the application component
        // can show optimistic "Planning backup..." status immediately. Skip during
        // active recovery — the backup runs but won't flow into the recovery
        // environment, so the optimistic message would mislead about its purpose.
        this.applicationFormBaseService.navigateToApplication(
          this.application.accountId,
          this.application.applicationId,
          !this.isInRecovery
        )
      }, this.applicationFormBaseService.handleSaveError())
  }

  // Track whether the dialog was opened by Cancel button (true) or route guard (false)
  private cancelButtonTriggered = false

  cancelClick() {
    if (this.hasUnsavedChanges) {
      this.cancelButtonTriggered = true
      this.unsavedChangesDialog.open()
    } else {
      this.navigateAway()
    }
  }

  // Called by the CanDeactivate guard when navigating away via sidenav or other routes
  showUnsavedChangesDialog(): Observable<boolean> {
    this.cancelButtonTriggered = false
    this.unsavedChangesDialog.open()
    return this.deactivateSubject.asObservable().pipe(first())
  }

  dismissUnsavedChangesDialog() {
    this.unsavedChangesDialog.close()
    if (this.refreshTriggered) {
      // Refresh was triggered - just close dialog and reset flag
      this.refreshTriggered = false
    } else if (!this.cancelButtonTriggered) {
      // Guard is waiting for response - emit false to block navigation
      this.deactivateSubject.next(false)
    }
  }

  confirmDiscardChanges() {
    this.unsavedChangesDialog.close()
    // Reset dirty state so the CanDeactivate guard doesn't re-trigger the dialog
    this._applicationFormModel.markAsPristine()
    this.accessMethodDirty = false
    if (this.refreshTriggered) {
      // Refresh was triggered - reload the page
      this.refreshTriggered = false
      this.performRefresh()
    } else if (this.cancelButtonTriggered) {
      // Cancel button was clicked - navigate manually
      this.navigateAway()
    } else {
      // Guard is waiting for response - emit true to allow navigation
      this.deactivateSubject.next(true)
    }
  }

  private navigateAway() {
    this.router.navigateByUrl(
      this.uiService.buildApplicationUrl(this.accountService.accountId, this.application.applicationId)
    )
  }

  navigateToIssues() {
    const appUrl = this.uiService.buildApplicationUrl(this.accountService.accountId, this.application.applicationId)
    this.router.navigateByUrl(`${appUrl}#issues`)
  }

  // Track if refresh was triggered and confirmed
  private refreshTriggered = false

  // Esc on the unsaved-changes modal maps to "Keep editing" (cancel), not
  // "Discard changes" — safer default since Esc elsewhere dismisses without
  // destroying work. Gated on isOpen so the listener is inert otherwise.
  @HostListener('document:keydown.escape', ['$event'])
  onUnsavedChangesEscape(event: KeyboardEvent): void {
    if (!this.unsavedChangesDialog?.isOpen) return
    event.preventDefault()
    this.dismissUnsavedChangesDialog()
  }

  // Intercept keyboard refresh shortcuts (F5, Ctrl+R, Cmd+R) to show custom dialog
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isRefreshKey = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r')

    if (isRefreshKey && this.hasUnsavedChanges) {
      event.preventDefault()
      this.refreshTriggered = true
      this.cancelButtonTriggered = false
      this.unsavedChangesDialog.open()
    }
  }

  // Handle refresh confirmation - called from confirmDiscardChanges when refreshTriggered
  private performRefresh(): void {
    window.location.reload()
  }

  // Check if there are any unsaved changes across all tabs
  get hasUnsavedChanges(): boolean {
    return this.applicationFormModel.dirty || this.accessMethodDirty
  }

  // Get a summary of pending changes for the unsaved changes dialog
  get pendingChanges(): { category: string; changes: string[] }[] {
    const changes: { category: string; changes: string[] }[] = []

    // General tab changes
    const generalChanges: string[] = []
    if (this.applicationFormModel.get('applicationName')?.dirty) {
      generalChanges.push('Application name')
    }
    if (this.applicationFormModel.get('applicationRpo')?.dirty) {
      generalChanges.push('Recovery Point Objective')
    }
    if (this.applicationFormModel.get('applicationNotificationEmails')?.dirty) {
      generalChanges.push('Notification emails')
    }
    if (this.applicationFormModel.get('applicationIntegrationIds')?.dirty) {
      generalChanges.push('Add-ons / Integrations')
    }
    if (this.accessMethodDirty) {
      generalChanges.push('Access method')
    }
    if (generalChanges.length > 0) {
      changes.push({ category: 'General Settings', changes: generalChanges })
    }

    // Resources tab changes
    const resourceChanges: string[] = []
    if (this.applicationFormModel.get('matchingRules')?.dirty) {
      resourceChanges.push('Inclusion rules')
    }
    if (this.applicationFormModel.get('excludingRules')?.dirty) {
      resourceChanges.push('Exclusion rules')
    }
    if (this.applicationFormModel.get('resourceSelections')?.dirty) {
      resourceChanges.push('Resource selections')
    }
    if (resourceChanges.length > 0) {
      changes.push({ category: 'Resources', changes: resourceChanges })
    }

    return changes
  }

  get refreshing() {
    return subscriptionActive(this.appSubscription) || subscriptionActive(this.sourceResourcesSubscription)
  }

  get saving() {
    return !!this.updateApplicationSubscription
  }

  get applicationName() {
    return this._applicationFormModel.value.applicationName as string
  }

  get rpo() {
    return parseFloat(this._applicationFormModel.value.applicationRpo)
  }

  get enableRealTimeRpo() {
    return this.accountService.entitledForCapability('realTimeRpos')
  }

  get rbacEnabled() {
    return this.accountService.rbacEnabled
  }

  get isRealTimeRpo() {
    return this.rpo === 0
  }

  get rpoText() {
    if (this.rpo === 0) {
      return 'Real-time replication'
    }
    return durationText(this.rpo)
  }

  get notificationEmails(): string[] {
    return this._applicationFormModel.value.applicationNotificationEmails
  }

  protected get applicationIntegrationIds() {
    return this._applicationFormModel.get('applicationIntegrationIds').value
  }

  deleteClick() {
    this.deleteApplicationDialog.open()
  }

  dismissDeleteModal() {
    this.deleteApplicationDialog.close()
  }

  deleteApplication() {
    this.deleteApplicationSubscription = this.application
      .delete$()
      .pipe(finalize(() => (this.deleteApplicationSubscription = null)))
      .subscribe(
        () => {
          this.deleteApplicationDialog.close()
          // Navigate back to the application page so deletion logic can handle the final redirect
          this.router.navigateByUrl(
            this.uiService.buildApplicationUrl(this.accountService.accountId, this.application.applicationId)
          )
        },
        (error: BaseError) => {
          this.deleteApplicationDialog.error =
            'Unable to delete this application. Please try again later. If the issue persists, please contact support.'
        }
      )
  }

  get deletingApplication() {
    return subscriptionActive(this.deleteApplicationSubscription)
  }

  get sourceAccessTemplate() {
    return this.accessTemplates.sourceTemplateS3Url
  }

  get accountId() {
    return this.accountService.accountId
  }

  get targetAccessTemplate() {
    return this.accessTemplates.targetTemplateS3Url
  }

  get sourceInstallCommand() {
    return this.accessTemplates?.sourceInstallCommand
  }

  get targetInstallCommand() {
    return this.accessTemplates?.targetInstallCommand
  }

  get failbackInstallCommand() {
    return this.accessTemplates?.failbackInstallCommand
  }

  get sourceEndpointUri() {
    return this.application.sourceEndpointUri
  }

  get targetEndpointUri() {
    return this.application.targetEndpointUri
  }

  get identityProviderId() {
    return this.application.identityProviderId
  }

  get accessChecksComplete() {
    return this.sourceAccessService.allGood && this.targetAccessService.allGood
  }

  /**
   *  Do not allow delete button by checking if:
   *  - The subscription is expired and if a
   *  - A manual recovery (Test or Restore) is in progress
   */
  get canDelete() {
    return (
      this.application && // Application must be loaded
      this.canDeleteApplicationPermission && // Must have delete permission
      !this.saving && // Not currently saving
      !this.accountService.subscriptionExpired && // Subscription is active
      !(
        (this.application.currentlyRestoring || this.application.currentlyFailingBack) && // Either restoring or failing back
        this.application.restoreTrigger === RestoreTriggerDto.MANUAL // And it was manually triggered
      ) &&
      this.application.syncPhase === SyncPhaseDto.STANDBY // App must be in the STANDBY state
    )
  }

  get isInRecovery(): boolean {
    return (
      !!this.application &&
      (this.application.syncPhase === SyncPhaseDto.FAILOVER ||
        this.application.syncPhase === SyncPhaseDto.FAILOVER_TEST)
    )
  }

  get isInActiveRecovery(): boolean {
    return !!this.application && this.application.syncPhase === SyncPhaseDto.FAILOVER
  }

  get isInTestRecovery(): boolean {
    return !!this.application && this.application.syncPhase === SyncPhaseDto.FAILOVER_TEST
  }

  get upgradeNeeded(): boolean {
    return (
      (this.sourceAccessService && !this.sourceAccessService.hasLatestVersion) ||
      (this.targetAccessService && !this.targetAccessService.hasLatestVersion)
    )
  }

  hasUpgradeDetails() {
    return (
      (this.sourceAccessService && this.sourceAccessService.missingChanges?.length > 0) ||
      (this.sourceAccessService &&
        this.targetAccessService &&
        this.releaseDetailsService.hasUpgradeDetails(
          this.sourceAccessService.versionNumber,
          this.targetAccessService.versionNumber
        ))
    )
  }

  upgradeDetails() {
    if (this.sourceAccessService && this.sourceAccessService.missingChanges?.length > 0) {
      return this.sourceAccessService.missingChanges.map((change) => {
        return {
          sourceDelegateVersion: 0,
          targetDelegateVersion: 0,
          description: change.description,
        }
      })
    }

    if (!!this.sourceAccessService && !!this.targetAccessService) {
      return this.releaseDetailsService.upgradeDetails(
        this.sourceAccessService.versionNumber,
        this.targetAccessService.versionNumber
      )
    }

    return []
  }

  get accountIntegrations() {
    return this.accountService.integrations || []
  }

  isValidEmail(value: string) {
    return emailAddressIsValid(value)
  }

  get applicationType(): ApplicationType {
    return this.application ? this.application.type : ApplicationType.AWS
  }

  get canModify(): boolean {
    return this.canModifyApplication
  }

  // Show Access tab for AWS apps when StackSets capability is enabled
  get showAccessTab(): boolean {
    return this.applicationType === ApplicationType.AWS && this.accountService.stacksetsCapabilityEnabled
  }

  // Current access method based on actual state from /access endpoint (source of truth)
  get currentAccessMethod(): AccessMethodType {
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    return sourceManaged && targetManaged ? ACCESS_METHOD.STACKSETS : ACCESS_METHOD.CLOUDFORMATION
  }

  // Migration intent from UIState (user's saved preference for switching methods)
  get migrationIntent(): AccessMethodType | null {
    if (!this.application) return null
    return this.migrationIntentService.getMigrationIntent(this.sourceEndpointUri, this.targetEndpointUri)
  }

  // True if user has saved a migration intent that differs from current actual state
  get isMigrating(): boolean {
    const intent = this.migrationIntent
    return intent !== null && intent !== this.currentAccessMethod
  }

  // Loading state for access method section - true while checking access status
  get accessMethodLoading(): boolean {
    return !this.application || this.testingAccess
  }

  // StackSets selected but not yet configured in AWS (neither stack managed by StackSet)
  get stackSetsNotConfigured(): boolean {
    if (this.selectedAccessMethod !== ACCESS_METHOD.STACKSETS) return false
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    return !sourceManaged && !targetManaged
  }

  // StackSets configured and working (both stacks managed by StackSet)
  get stackSetsConfigured(): boolean {
    if (this.selectedAccessMethod !== ACCESS_METHOD.STACKSETS) return false
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    return sourceManaged && targetManaged
  }

  // CloudFormation selected but stacks are managed by StackSets (need to deploy standalone CF stacks)
  get cloudFormationNotConfigured(): boolean {
    if (this.selectedAccessMethod !== ACCESS_METHOD.CLOUDFORMATION) return false
    // Need CloudFormation if current stacks are managed by StackSet
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    // If either stack is managed by StackSet, user needs to deploy standalone CF stacks
    return sourceManaged || targetManaged
  }

  // CloudFormation is fully configured (stacks exist and are NOT managed by StackSets)
  get cloudFormationConfigured(): boolean {
    if (this.selectedAccessMethod !== ACCESS_METHOD.CLOUDFORMATION) return false
    const sourceHasStack = !!this.sourceAccessService?.stack
    const targetHasStack = !!this.targetAccessService?.stack
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    // Configured if stacks exist and NOT managed by StackSets
    return sourceHasStack && targetHasStack && !sourceManaged && !targetManaged
  }

  get cloudFormationAccessLost(): boolean {
    if (this.selectedAccessMethod !== ACCESS_METHOD.CLOUDFORMATION) return false
    const sourceFailed = this.sourceAccessService?.state === AccessState.FAILED
    const targetFailed = this.targetAccessService?.state === AccessState.FAILED
    return sourceFailed || targetFailed
  }

  // StackSets partially configured (one stack managed, one not)
  get stackSetsPartiallyConfigured(): boolean {
    if (this.selectedAccessMethod !== ACCESS_METHOD.STACKSETS) return false
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    return (sourceManaged && !targetManaged) || (!sourceManaged && targetManaged)
  }

  // Check if source has CloudFormation stack but not managed by StackSet
  get sourceHasCfnButNotStackSet(): boolean {
    const hasStack = !!this.sourceAccessService?.stack
    const managedByStackSet = this.sourceAccessService?.managedByStackSet ?? false
    return hasStack && !managedByStackSet
  }

  // Check if target has CloudFormation stack but not managed by StackSet
  get targetHasCfnButNotStackSet(): boolean {
    const hasStack = !!this.targetAccessService?.stack
    const managedByStackSet = this.targetAccessService?.managedByStackSet ?? false
    return hasStack && !managedByStackSet
  }

  // Source managed by StackSet
  get sourceManagedByStackSet(): boolean {
    return this.sourceAccessService?.managedByStackSet ?? false
  }

  // Target managed by StackSet
  get targetManagedByStackSet(): boolean {
    return this.targetAccessService?.managedByStackSet ?? false
  }

  // Select access method and mark as dirty for save
  selectAccessMethod(method: AccessMethodType): void {
    if (this.selectedAccessMethod === method) {
      return
    }
    this.selectedAccessMethod = method
    this.accessMethodDirty = method !== this.currentAccessMethod
    // Force tabs to rebuild to show/hide Configure Access tab
    this._tabs = []
  }

  // Check if user needs to configure access for the selected method
  // This is true when user switched to StackSets but StackSets aren't configured,
  // OR when user switched to CloudFormation but stacks are StackSet-managed
  get needsAccessConfiguration(): boolean {
    if (!this.showAccessTab) return false
    // StackSets selected but not configured
    if (this.selectedAccessMethod === ACCESS_METHOD.STACKSETS && !this.stackSetsConfigured) return true
    // CloudFormation selected but stacks are StackSet-managed (need standalone CF)
    if (this.selectedAccessMethod === ACCESS_METHOD.CLOUDFORMATION && this.cloudFormationNotConfigured) return true
    // CloudFormation selected but access stacks have been removed
    if (this.cloudFormationAccessLost) return true
    return false
  }

  // Dynamic tabs - includes Upgrade or Configure Access tab when needed
  // Cached to avoid creating new array references on every change detection cycle
  get tabs(): TabConfig[] {
    const isLoading = !this.sourceAccessService || !this.targetAccessService
    const showUpgrade = this.upgradeNeeded && this.canModify
    const needsAccessConfig = this.needsAccessConfiguration && this.canModify

    const shouldRebuild =
      this._tabs.length === 0 ||
      this._lastUpgradeNeeded !== showUpgrade ||
      this._lastUpgradeLoading !== isLoading ||
      this._lastNeedsAccessConfig !== needsAccessConfig

    if (shouldRebuild) {
      this._lastUpgradeNeeded = showUpgrade
      this._lastUpgradeLoading = isLoading
      this._lastNeedsAccessConfig = needsAccessConfig

      this._tabs = [
        { label: 'Resources', route: 'resources' },
        { label: 'General', route: '' },
      ]

      if (isLoading) {
        this._tabs.push({
          label: 'Checking access templates...',
          route: 'upgrade',
          loading: true,
        })
      } else if (needsAccessConfig) {
        // Show Configure Access tab when user is changing access method
        // (takes precedence over upgrade since access must be configured first)
        this._tabs.push({
          label: 'Configure Access',
          route: 'upgrade',
          icon: 'shield',
        })
      } else if (showUpgrade) {
        // Show Upgrade tab with icon when templates need updating
        this._tabs.push({
          label: 'Upgrade',
          route: 'upgrade',
          icon: 'cloud-upload',
        })
      }
    }
    return this._tabs
  }

  // StackSets-related getters for upgrade tab
  get stackSetsEnabled(): boolean {
    return !!(
      this.accessTemplates?.stackSetsTemplate?.sourceStackSetTemplateS3Url ||
      this.accessTemplates?.stackSetsTemplate?.targetStackSetTemplateS3Url ||
      this.accessTemplates?.stackSetsTemplate?.failbackStackSetTemplateS3Url
    )
  }

  get sourceStackSetTemplateUrl(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.sourceStackSetTemplateS3Url
  }

  get targetStackSetTemplateUrl(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.targetStackSetTemplateS3Url
  }

  get sourceTemplateBucket(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.sourceTemplate?.bucket
  }

  get sourceTemplateKey(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.sourceTemplate?.key
  }

  get targetTemplateBucket(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.targetTemplate?.bucket
  }

  get targetTemplateKey(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.targetTemplate?.key
  }

  get regenerationJobId(): string | null {
    return this.accessTemplates?.stackSetsTemplate?.jobId ?? null
  }

  get sourceAccountId(): string {
    return this.sourceEndpointUri?.split(':')[1]
  }

  get sourceRegion(): string {
    return this.sourceEndpointUri?.split(':')[2]
  }

  get targetAccountId(): string {
    return this.targetEndpointUri?.split(':')[1]
  }

  get targetRegion(): string {
    return this.targetEndpointUri?.split(':')[2]
  }

  // Determine if we need full StackSets setup (migration from CF) vs just update
  // Setup mode: neither stack is managed by StackSet yet
  // Note: Partially configured (one managed, one not) intentionally shows setup flow
  // so user completes full setup before seeing simpler update instructions
  get needsStackSetsSetup(): boolean {
    const sourceManaged = this.sourceAccessService?.managedByStackSet ?? false
    const targetManaged = this.targetAccessService?.managedByStackSet ?? false
    if (!sourceManaged || !targetManaged) return true
    // Both managed — don't auto-switch until user acknowledges
    return !this.stackSetsSetupAcknowledged
  }

  onFinishStackSetsSetup(): void {
    this.stackSetsSetupAcknowledged = true
    // Migration is controlled by the backend — clear form dirty state
    // so the user isn't prompted to save changes that don't apply
    this._applicationFormModel.markAsPristine()
    this.accessMethodDirty = false
  }

  // Testing state for passing to stacksets-instructions
  get isTestingSourceAccess(): boolean {
    return this.sourceAccessService?.subscription != null
  }

  get isTestingTargetAccess(): boolean {
    return this.targetAccessService?.subscription != null
  }

  // Handle test access events from stacksets-instructions
  onTestAccess(scope: 'source' | 'target'): void {
    if (scope === 'source') {
      this.sourceAccessService?.validateAccess()
    } else {
      this.targetAccessService?.validateAccess()
    }
  }

  get loadingAccessTemplates(): boolean {
    return subscriptionActive(this.accessTemplatesSubscription) || subscriptionActive(this.accessStatusSubscription)
  }

  navigateToAccessMethod(): void {
    const generalTabIndex = this.tabs.findIndex((t) => t.route === '')
    if (generalTabIndex !== -1) {
      this.onTabChange(generalTabIndex)
      setTimeout(() => {
        const element = document.getElementById('access-method-section')
        if (element) {
          const headerElement = document.querySelector('.header-container')
          const tabsElement = document.querySelector('app-tabs .tabs-container')
          let offset = 0
          if (headerElement) {
            offset += headerElement.getBoundingClientRect().height
          }
          if (tabsElement) {
            offset += tabsElement.getBoundingClientRect().height
          }
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth',
          })
        }
      }, 50)
    }
  }

  navigateToUpgradeTab(): void {
    const upgradeTabIndex = this.tabs.findIndex((t) => t.route === 'upgrade')
    if (upgradeTabIndex !== -1) {
      this.onTabChange(upgradeTabIndex)
    }
  }

  private fetchAccessTemplates(): void {
    this.accessTemplatesSubscription = this.apiService
      .getAccessTemplates(
        this.accountService.accountId,
        this.sourceEndpointUri,
        this.targetEndpointUri,
        this.identityProviderId
      )
      .pipe(finalize(() => (this.accessTemplatesSubscription = null)))
      .subscribe(
        (accessTemplatesDto) => (this.accessTemplates = accessTemplatesDto),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  checkForExistingAppWithSameName(form: AbstractControl): ValidationErrors | null {
    const appName = form.get('applicationName').value
    const appId = this.application ? this.application.applicationId : null

    if (appName && appId) {
      const apps = this.applicationProviderService.loadedApplications()
      if (apps.some((a) => a.name == appName && a.applicationId != appId)) {
        return { existingAppWithSameName: appName }
      }
    }

    return null
  }

  onTabChange(tabIndex: number) {
    this.activeTabIndex = tabIndex
    const tabRoute = this.tabs[tabIndex]?.route
    if (tabRoute === 'resources') {
      this.activeTab = 'resources'
    } else if (tabRoute === 'upgrade') {
      this.activeTab = 'upgrade'
      // Fetch access templates when switching to upgrade tab
      if (!this.accessTemplates && !subscriptionActive(this.accessTemplatesSubscription)) {
        this.fetchAccessTemplates()
      }
    } else {
      this.activeTab = 'general'
    }

    // Scroll to the tab content after Angular updates the DOM
    setTimeout(() => {
      let elementId: string
      if (this.activeTab === 'resources') {
        elementId = 'resources-tab-content'
      } else if (this.activeTab === 'upgrade') {
        elementId = 'upgrade-tab-content'
      } else {
        elementId = 'general-tab-content'
      }
      const element = document.getElementById(elementId)
      if (element) {
        // Calculate offset for sticky header and tabs
        const headerElement = document.querySelector('.header-container')
        const tabsElement = document.querySelector('app-tabs .tabs-container')

        let offset = 0
        if (headerElement) {
          offset += headerElement.getBoundingClientRect().height
        }
        if (tabsElement) {
          offset += tabsElement.getBoundingClientRect().height
        }

        // Get element position and scroll with offset
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth',
        })
      }
    }, 0)
  }
}
